'use client';

import type { CommerceModel } from 'api-types';
import { useEffect, useRef } from 'react';
import type { RelewiseProduct } from '~/components/features';
import { productListItemToGAItem } from '../productMappers/productListItemToGAItem';
import { relewiseProductToGAItem } from '../productMappers/relewiseProductToGAItem';
import { sendGTMEvent } from '../sendGTMEvent';
import type { ItemListType } from '../types';
import { useGTMQueue } from '../useGTMQueue';

type Props = {
  products?: CommerceModel.LineItem[] | RelewiseProduct[];
  headline?: string;
  itemListId?: string;
  itemListName?: string;
  itemListType?: ItemListType;
};

/**
 * Handles tracking of view_item_list event
 *
 * Expected to be rendered alongside any list of products that should send a view_item_list event.
 */

// Type guard to check if product is a RelewiseProduct
function isRelewiseProduct(product: any): product is RelewiseProduct {
  return product && typeof product.productId === 'string' && 'displayName' in product;
}

export const ViewItemListTracker = ({
  products,
  headline,
  itemListId,
  itemListType,
  itemListName,
}: Props) => {
  const { addToQueue } = useGTMQueue();
  const lastTrackedCount = useRef<number>(0);

  // Provide defaults when tracking props are not explicitly provided
  const finalItemListId = itemListId || 'product-list-page';
  const finalItemListType = itemListType || 'ProductListPage';
  const finalItemListName = itemListName || headline || 'Product List';

  useEffect(() => {
    const currentProductCount = products?.length || 0;
    const shouldNotTrack = !currentProductCount || currentProductCount <= lastTrackedCount.current;

    if (shouldNotTrack) {
      return;
    }

    // Only track the new products that haven't been tracked yet
    const newProducts = products?.slice(lastTrackedCount.current) || [];

    if (!newProducts.length) {
      return;
    }

    // Use to limit size of request
    const chunkSize = 24;
    for (let i = 0; i < newProducts.length; i += chunkSize) {
      const items = newProducts
        .slice(i, i + chunkSize)
        .map((product: CommerceModel.LineItem | RelewiseProduct, index: number) => {
          if (isRelewiseProduct(product)) {
            return relewiseProductToGAItem(product, {
              index: lastTrackedCount.current + i + index,
              listId: finalItemListId,
              listName: finalItemListName,
              listType: finalItemListType,
            });
          } else {
            return productListItemToGAItem(product as CommerceModel.LineItem, {
              index: lastTrackedCount.current + i + index,
              listId: finalItemListId,
              listName: finalItemListName,
              listType: finalItemListType,
            });
          };
        });

      addToQueue(() => {
        sendGTMEvent({
          event: 'view_item_list',
          ecommerce: {
            items,
          },
        });
      });
    }

    // Update the count of tracked products
    lastTrackedCount.current = currentProductCount;
  }, [products]);

  return null;
};